import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, getEmptyFilterExpression } from '@fabric-ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SoarQueryKeys } from '../queryKeys';
import { AutomationRuleCreateDto, AutomationRuleDto, automationRuleService } from '../services/automationRuleService';
import { catalogService } from '../services/catalogService';
import { AutomationRule } from '../types';
import { backendToFrontendFilter, frontendToBackendFilter } from '../utils/filterTransform';

export interface ConditionField {
  id: string;
  label: string;
  type: 'text' | 'select';
  options?: string[];
}

const STORAGE_KEY = 'soar_new_automation_rule_editing';

export interface NewAutomationRuleState {
  editingRuleId: string | null;
  currentRuleToLoad: AutomationRule | null;
  name: string;
  description: string;
  entityType?: string;
  triggerType?: string;
  selectedPlaybookIds: string[];
  isActive: boolean;
  filters: Filter;
  updateFilters: Filter;
  isSaving: boolean;
}

export const useNewAutomationRule = (onRuleSaved?: (rule: AutomationRule, isNew: boolean) => void) => {
  const queryClient = useQueryClient();
  const initialState: NewAutomationRuleState = {
    editingRuleId: null,
    currentRuleToLoad: null,
    name: '',
    description: '',
    entityType: undefined,
    triggerType: undefined,
    selectedPlaybookIds: [],
    isActive: true,
    filters: getEmptyFilterExpression(),
    updateFilters: getEmptyFilterExpression(),
    isSaving: false,
  };

  const [state, setState] = useState<NewAutomationRuleState>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return { ...initialState, ...JSON.parse(saved) };
      } catch (e) {
        console.error('Failed to load new rule state', e);
      }
    }
    return initialState;
  });

  const { data: events = [], isLoading: isLoadingEvents } = useQuery({
    queryKey: [SoarQueryKeys.CATALOG_EVENTS, state.entityType],
    queryFn: () => catalogService.getEvents(state.entityType!),
    enabled: !!state.entityType,
  });

  const { data: conditions = [], isLoading: isLoadingConditions } = useQuery({
    queryKey: [SoarQueryKeys.CATALOG_CONDITIONS, state.entityType],
    queryFn: () => catalogService.getConditions(state.entityType!),
    enabled: !!state.entityType,
  });

  const persist = useCallback((newState: Partial<NewAutomationRuleState>) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      const toPersist = {
        editingRuleId: updated.editingRuleId,
        name: updated.name,
        description: updated.description,
        entityType: updated.entityType,
        triggerType: updated.triggerType,
        selectedPlaybookIds: updated.selectedPlaybookIds,
        isActive: updated.isActive,
        filters: updated.filters,
        updateFilters: updated.updateFilters,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
      return updated;
    });
  }, []);

  // Transform and load filters when conditions or the rule to load changes
  useEffect(() => {
    if (state.currentRuleToLoad && conditions.length > 0) {
      const rule = state.currentRuleToLoad;

      const isUpdateNode = (node: any): boolean => {
        if (!node) return false;
        if (node.operator === 'CHANGE') return true;
        if (Array.isArray(node.rules)) return node.rules.some(isUpdateNode);
        return false;
      };

      const splitRules = (rawFilter: any) => {
        if (!rawFilter || !Array.isArray(rawFilter.rules)) {
          return { conditionRules: [], updateRules: [] };
        }

        const conditionRules = rawFilter.rules.filter((r: any) => !isUpdateNode(r));
        const updateRules = rawFilter.rules.filter((r: any) => isUpdateNode(r));
        return { conditionRules, updateRules };
      };

      const { conditionRules, updateRules } = splitRules(rule.rawFilter);

      const transformedFilters =
        rule.filters || backendToFrontendFilter({ rules: conditionRules, operator: rule.rawFilter?.operator || 'AND' }, conditions);

      const transformedUpdateFilters =
        updateRules.length > 0 ? backendToFrontendFilter({ rules: updateRules, operator: 'AND' }, conditions) : getEmptyFilterExpression();

      persist({
        filters: transformedFilters,
        updateFilters: transformedUpdateFilters,
        // Reset the trigger to avoid infinite loop
        currentRuleToLoad: null,
      });
    }
  }, [state.currentRuleToLoad, conditions, persist]);

  const setName = useCallback((name: string) => persist({ name }), [persist]);
  const setIsActive = useCallback((isActive: boolean) => persist({ isActive }), [persist]);
  const setDescription = useCallback((description: string) => persist({ description }), [persist]);

  const setEntityType = useCallback(
    (entityType: string) => {
      persist({
        entityType,
        triggerType: undefined,
        filters: getEmptyFilterExpression(),
        updateFilters: getEmptyFilterExpression(),
      });
    },
    [persist]
  );

  const setTriggerType = useCallback((triggerType: string) => persist({ triggerType }), [persist]);

  const setFilters = useCallback((filters: Filter) => persist({ filters }), [persist]);

  const setUpdateFilters = useCallback((updateFilters: Filter) => persist({ updateFilters }), [persist]);

  const togglePlaybook = useCallback(
    (playbookId: string) => {
      const isSelected = state.selectedPlaybookIds.includes(playbookId);
      persist({
        selectedPlaybookIds: isSelected
          ? state.selectedPlaybookIds.filter(id => id !== playbookId)
          : [...state.selectedPlaybookIds, playbookId],
      });
    },
    [state.selectedPlaybookIds, persist]
  );

  const removePlaybook = useCallback(
    (playbookId: string) => {
      persist({
        selectedPlaybookIds: state.selectedPlaybookIds.filter(id => id !== playbookId),
      });
    },
    [state.selectedPlaybookIds, persist]
  );

  const reorderPlaybooks = useCallback(
    (startIndex: number, endIndex: number) => {
      const result = state.selectedPlaybookIds.filter((_, idx) => idx !== startIndex);
      const removed = state.selectedPlaybookIds[startIndex];
      persist({
        selectedPlaybookIds: [...result.slice(0, endIndex), removed, ...result.slice(endIndex)],
      });
    },
    [state.selectedPlaybookIds, persist]
  );

  const resetForm = useCallback(() => {
    const resetState: Partial<NewAutomationRuleState> = {
      editingRuleId: null,
      name: '',
      description: '',
      entityType: undefined,
      triggerType: undefined,
      selectedPlaybookIds: [],
      isActive: true,
      filters: getEmptyFilterExpression(),
      updateFilters: getEmptyFilterExpression(),
    };
    persist(resetState);
  }, [persist]);

  const loadRule = useCallback(
    (rule: AutomationRule) => {
      persist({
        editingRuleId: rule.id,
        currentRuleToLoad: rule, // Trigger the transformation useEffect
        name: rule.name || '',
        description: rule.description || '',
        entityType: rule.entityType || undefined,
        triggerType: rule.triggerType || undefined,
        selectedPlaybookIds: [...(rule.playbookIds || [])],
        isActive: rule.status === 'Active',
      });
    },
    [persist]
  );

  const isValid = useMemo(
    () =>
      (state.name?.trim()?.length || 0) > 0 && (state.entityType?.trim()?.length || 0) > 0 && (state.triggerType?.trim()?.length || 0) > 0,
    [state.name, state.entityType, state.triggerType]
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!isValid || !state.entityType || !state.triggerType) return null;

      // Combine both logical builders into a single logical AND structure
      const combinedFrontendFilter: Filter = {
        and: {
          operands: [state.filters, state.updateFilters],
        },
      };

      const ruleData: AutomationRuleCreateDto = {
        name: state.name,
        description: state.description,
        active: state.isActive,
        triggerEntity: state.entityType!,
        triggerEvent: state.triggerType!,
        playbookIds: [...state.selectedPlaybookIds],
        filter: frontendToBackendFilter(combinedFrontendFilter),
      };

      const isNew = !state.editingRuleId;
      const savedRule: AutomationRule = await (async () => {
        if (state.editingRuleId) {
          const updateDto: Partial<AutomationRuleDto> = {
            name: state.name,
            description: state.description,
            active: state.isActive,
            triggerEntity: state.entityType,
            triggerEvent: state.triggerType,
            playbookIds: [...state.selectedPlaybookIds],
            filter: ruleData.filter,
          };
          await automationRuleService.updateAutomationRule(state.editingRuleId, updateDto);

          return {
            id: state.editingRuleId!,
            name: state.name,
            description: state.description,
            status: state.isActive ? 'Active' : 'Inactive',
            lastModified: new Date().toISOString(),
            entityType: state.entityType!,
            triggerType: state.triggerType!,
            playbookIds: [...state.selectedPlaybookIds],
            filters: state.filters,
            updateFilters: state.updateFilters,
            rawFilter: ruleData.filter,
          };
        }
        const createdRule = await automationRuleService.createAutomationRule(ruleData);

        return {
          id: createdRule.ruleId,
          name: createdRule.name,
          description: createdRule.description,
          status: createdRule.active ? 'Active' : 'Inactive',
          lastModified: createdRule.updatedAt || createdRule.createdAt || new Date().toISOString(),
          entityType: createdRule.triggerEntity,
          triggerType: createdRule.triggerEvent,
          playbookIds: createdRule.playbooks?.map(p => p.playbookId) || [],
          filters: state.filters,
          updateFilters: state.updateFilters,
          rawFilter: createdRule.filter,
        };
      })();

      return { savedRule, isNew };
    },
    onSuccess: data => {
      if (data && onRuleSaved) {
        onRuleSaved(data.savedRule, data.isNew);
      }
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.AUTOMATION_RULES] });
    },
  });

  const triggerOptions = useMemo(() => events.map(e => ({ title: e.displayName || e.eventName || e.eventId, value: e.eventId })), [events]);

  const availableConditionFields = useMemo(
    (): ConditionField[] =>
      conditions.map(c => ({
        id: c.fieldKey,
        label: c.fieldKey.split('.').pop() || c.fieldKey,
        type: c.fieldType.toLowerCase() === 'enum' ? 'select' : 'text',
      })),
    [conditions]
  );

  return {
    ...state,
    isValid,
    triggerOptions,
    availableConditionFields,
    isLoadingEvents,
    isLoadingConditions,
    isSaving: saveMutation.isPending,
    setName,
    setIsActive,
    setDescription,
    setEntityType,
    setTriggerType,
    setFilters,
    setUpdateFilters,
    togglePlaybook,
    removePlaybook,
    reorderPlaybooks,
    saveAutomationRule: saveMutation.mutateAsync,
    loadRule,
    resetForm,
  };
};
